import React from 'react';
import { Clock, RotateCcw, ShieldCheck } from 'lucide-react';
import { TaapProposal } from '../../types/taap';
import {
  formatMoney,
  formatNumber,
  PLATFORM_LABELS,
  PRIORITY_LABELS,
  PROPOSAL_TYPE_LABELS,
} from '../../utils/taapFormat';

interface Props {
  proposal: TaapProposal;
  currency?: string;
  busy?: boolean;
  onApprove?: (proposal: TaapProposal) => void;
  onReject?: (proposal: TaapProposal) => void;
  onOpenDetail?: (proposal: TaapProposal) => void;
  /** ダッシュボードでは要点だけを出す。 */
  compact?: boolean;
}

const PRIORITY_STYLES: Record<string, string> = {
  high: 'bg-destructive/10 text-red-700 border-destructive/20',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200',
};

/**
 * 提案1件。
 *
 * 「何が起きているか」より先に「承認すると何が変わるか」「元に戻せるか」を
 * 見せる。承認は配信を変える操作なので、押す前に取り消せるかどうかが
 * 判断材料として要る。
 */
const TaapProposalCard: React.FC<Props> = ({
  proposal,
  currency = 'JPY',
  busy = false,
  onApprove,
  onReject,
  onOpenDetail,
  compact = false,
}) => {
  const target = [
    proposal.target.platform ? PLATFORM_LABELS[proposal.target.platform] ?? proposal.target.platform : null,
    proposal.target.campaign_name,
    proposal.target.adset_name,
  ]
    .filter(Boolean)
    .join(' · ');

  const saving = proposal.expected_effect?.monthly_saving;
  const conversionsDelta = proposal.expected_effect?.conversions_delta;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-md border px-2 py-0.5 text-xs font-medium ${PRIORITY_STYLES[proposal.priority] ?? PRIORITY_STYLES.low}`}>
          {PRIORITY_LABELS[proposal.priority] ?? proposal.priority}
        </span>
        <span className="text-xs text-gray-600">{PROPOSAL_TYPE_LABELS[proposal.type] ?? proposal.type}</span>
        {proposal.reversible && (
          <span className="flex items-center gap-1 text-xs text-green-700">
            <RotateCcw className="h-3 w-3" />
            復元できます
          </span>
        )}
        <span className="ml-auto flex items-center gap-1 text-xs text-gray-500">
          <Clock className="h-3 w-3" />
          実行 約{proposal.estimated_minutes}分
        </span>
      </div>

      <h3 className="mt-2 text-base font-semibold text-gray-900">{proposal.title}</h3>
      {target && <div className="mt-0.5 text-xs text-gray-500">対象: {target}</div>}

      <p className="mt-2 text-sm leading-relaxed text-gray-700">{proposal.description}</p>

      {!compact && proposal.change_summary && (
        <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">
          <span className="font-medium">変更内容: </span>
          {proposal.change_summary}
        </div>
      )}

      {!compact && proposal.evidence?.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {proposal.evidence.map((signal) => (
            <li key={signal.key} className="flex flex-wrap items-baseline gap-2 text-xs">
              <span className="rounded bg-destructive/10 px-1.5 py-0.5 font-medium text-red-700">シグナル点灯</span>
              <span className="text-gray-800">{signal.display}</span>
              <span className="text-gray-400">（{signal.threshold_display}）</span>
            </li>
          ))}
        </ul>
      )}

      {(saving !== undefined || conversionsDelta !== undefined) && (
        <div className="mt-3">
          <div className="flex flex-wrap items-center gap-3 text-sm font-medium text-green-700">
            {conversionsDelta !== undefined && conversionsDelta > 0 && (
              <span>CV +{formatNumber(conversionsDelta)}件/月</span>
            )}
            {saving !== undefined && saving > 0 && <span>最大 {formatMoney(saving, currency)}/月</span>}
          </div>
          {/* 見込みは「目標まで完全に改善した場合」の理論値。実測は下回るのが
              普通なので、断定的な予測に見せない */}
          <div className="mt-0.5 text-[11px] text-gray-500">
            {proposal.expected_effect?.basis === 'return_to_target'
              ? '目標CPAまで戻った場合の試算です。実際の効果は実行7日後に計測します。'
              : proposal.expected_effect?.basis === 'baseline_recovery'
                ? 'このクリエイティブ自身の平常時まで回復した場合の試算です。実際の効果は実行7日後に計測します。'
                : '試算値です。実際の効果は実行7日後に計測します。'}
          </div>
        </div>
      )}

      {proposal.status === 'rejected' && proposal.rejected_reason && (
        <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
          却下理由: {proposal.rejected_reason}
        </div>
      )}

      {proposal.status === 'pending' && (onApprove || onReject || onOpenDetail) && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {onOpenDetail && (
            <button
              type="button"
              onClick={() => onOpenDetail(proposal)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              詳細と根拠を見る
            </button>
          )}
          {onReject && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onReject(proposal)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              却下
            </button>
          )}
          {onApprove && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onApprove(proposal)}
              className="ml-auto flex items-center gap-1.5 rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
            >
              <ShieldCheck className="h-4 w-4" />
              提案を承認
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo<Props>(TaapProposalCard);
